import { css, unsafeCSS } from 'lit';

import { GRAPH_COLOURS } from './graph-palette.js';
import { nsReducedMotionStyles } from '../ns-styles/ns-motion-styles.js';

/** Shared with `graph-chrome.ts`'s render helpers — moved out of `view-connections.styles.ts` so
 * the companies-directory graph gets pixel-identical zoom controls/legend/quick-look chrome
 * without reimplementing the CSS. Some selectors here (risk-* swatches) are only ever produced by
 * the connections graph's perspective switcher; harmless, unused CSS for any other consumer since
 * it's scoped per-component via shadow DOM. */
export const graphChromeStyles = css`
  .breadcrumbs-bar {
    position: absolute;
    left: 13px;
    top: 13px;
    max-width: min(72vw, 680px);
    padding: var(--wa-space-2xs) var(--wa-space-xs);
    border-radius: var(--wa-border-radius-pill);
    border: 1px solid var(--wa-color-surface-border);
    background-color: var(--wa-color-surface-raised);
    box-shadow: var(--ns-elevation-4);
    z-index: var(--ns-breadcrumbs-z-index, 889);
  }

  .pearls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--wa-space-2xs);
    margin: 0;
    padding: 0;
  }

  .pearl {
    border: 0;
    border-radius: var(--wa-border-radius-pill);
    padding: var(--wa-space-2xs) var(--wa-space-xs);
    color: var(--wa-color-text-normal);
    font: inherit;
    font-size: var(--wa-font-size-2xs);
    font-weight: 500;
    line-height: normal;
    background: var(--wa-color-neutral-fill-quiet);
    cursor: pointer;
  }

  .pearl[active] {
    background: var(--wa-color-brand-fill-loud);
    color: var(--wa-color-brand-on-loud);
  }

  .pearl-sep {
    color: var(--wa-color-text-quiet);
    font-size: var(--wa-font-size-2xs);
  }

  .quick-look {
    position: absolute;
    z-index: 889;
    pointer-events: none;
    min-width: 180px;
    background: var(--wa-color-surface-raised);
    border: 1px solid var(--wa-color-brand-border-normal);
    border-radius: var(--wa-border-radius-l);
    padding: var(--wa-space-s);
    color: var(--wa-color-text-normal);
    box-shadow: var(--ns-elevation-8);
    transform: translate(18px, -50%);
  }

  /* A card with a button takes the pointer, so the button can be reached and clicked */
  .quick-look--interactive {
    pointer-events: auto;
  }

  .ql-header {
    font-size: var(--wa-font-size-s);
    font-weight: 600;
    margin-bottom: var(--wa-space-xs);
    color: var(--wa-color-brand-on-quiet);
  }

  .ql-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--wa-space-m);
    padding: var(--wa-space-3xs) 0;
    font-size: var(--wa-font-size-xs);
    border-top: 1px solid var(--wa-color-surface-border);
  }

  .ql-label {
    color: var(--wa-color-text-quiet);
  }

  .ql-value {
    font-weight: 600;
    color: var(--wa-color-text-normal);
  }

  .ql-value.ql-active {
    color: var(--wa-color-danger-on-quiet);
  }

  .ql-value.ql-sic {
    font-family: monospace;
    font-size: var(--wa-font-size-2xs);
    color: var(--wa-color-text-quiet);
    letter-spacing: 0.04em;
  }

  .legend {
    position: absolute;
    bottom: 16px;
    left: 16px;
    z-index: 800;
    background-color: var(--wa-color-surface-raised);
    border: 1px solid var(--wa-color-surface-border);
    box-shadow: var(--ns-elevation-4);
    border-radius: var(--wa-border-radius-l);
    padding: var(--wa-space-xs) var(--wa-space-s);
    pointer-events: none;
    display: flex;
    flex-direction: column;
    gap: var(--wa-space-2xs);
    min-width: 148px;
  }

  .legend-title {
    font-size: var(--wa-font-size-3xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--wa-color-text-quiet);
    margin-bottom: var(--wa-space-3xs);
  }

  .legend-row {
    display: flex;
    align-items: center;
    gap: var(--wa-space-xs);
    font-size: var(--wa-font-size-2xs);
    color: var(--wa-color-text-normal);
  }

  .legend-swatch {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: var(--wa-border-radius-circle);
    background-color: var(--wa-color-surface-default);
  }

  .legend-swatch.risk-high {
    border: 2.5px solid ${unsafeCSS(GRAPH_COLOURS.risk.high)};
  }
  .legend-swatch.risk-medium {
    border: 2.5px solid ${unsafeCSS(GRAPH_COLOURS.risk.medium)};
  }
  .legend-swatch.risk-low {
    border: 2.5px solid ${unsafeCSS(GRAPH_COLOURS.risk.low)};
  }
  .legend-swatch.risk-unscored {
    border: 2.5px solid ${unsafeCSS(GRAPH_COLOURS.risk.unscored)};
  }
  .legend-swatch.risk-unknown {
    border: 2.5px solid ${unsafeCSS(GRAPH_COLOURS.risk.unknown)};
  }
  .legend-swatch.indirect {
    border: 2.5px dashed ${unsafeCSS(GRAPH_COLOURS.lineStrong)};
  }
  .legend-swatch.direct {
    border: 2.5px solid ${unsafeCSS(GRAPH_COLOURS.lineStrong)};
  }

  .legend-divider {
    height: 1px;
    background: var(--wa-color-surface-border);
    margin: var(--wa-space-3xs) 0;
  }

  @media (max-width: 576px) {
    /* Purely informational chrome — drop it on very narrow screens rather than compete for space
     * with the graph itself and .zoom-controls in the same bottom corner region. */
    .legend {
      display: none;
    }

    /* Pinch-zoom (already pointer-normalized via Cytoscape) covers in/out at this width — only
     * keep Fit, which pinch/scroll can't replicate. */
    .zoom-controls .zoom-in-btn,
    .zoom-controls .zoom-out-btn {
      display: none;
    }
  }

  .zoom-controls {
    position: absolute;
    bottom: 16px;
    right: 16px;
    z-index: 800;
    display: flex;
    flex-direction: column;
    gap: var(--wa-space-2xs);
    background-color: var(--wa-color-surface-raised);
    border: 1px solid var(--wa-color-surface-border);
    box-shadow: var(--ns-elevation-4);
    border-radius: var(--wa-border-radius-l);
    padding: var(--wa-space-2xs);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* Defined here rather than in the app's button styles, which the companies and relationship graphs omit. */
  wa-button.graph-button {
    --wa-form-control-height: 26px;
    --wa-form-control-border-radius: var(--wa-border-radius-m);
    --wa-form-control-border-width: 1px;
    --wa-focus-ring: solid 2px var(--wa-color-focus);
    --wa-focus-ring-offset: 2px;

    display: inline-flex;

    font-size: var(--wa-font-size-2xs);
  }

  wa-button.graph-button::part(base) {
    min-width: 0;
    padding: 0 var(--wa-space-xs);
    border: 1px solid var(--wa-color-neutral-border-normal);
    border-radius: var(--wa-border-radius-m);
    background: transparent;
    color: var(--graph-button-color, var(--wa-color-text-normal));
    font-weight: 600;
    transition:
      background 140ms,
      color 140ms,
      border-color 140ms;
  }

  wa-button.graph-button::part(base):hover {
    background: var(--wa-color-neutral-fill-quiet);
    color: var(--wa-color-text-normal);
  }

  wa-button.graph-button[pressed]::part(base) {
    border-color: var(--wa-color-brand-border-loud);
    background: var(--wa-color-brand-fill-loud);
    color: var(--wa-color-brand-on-loud);
  }

  wa-button.graph-button--primary::part(base) {
    border-color: transparent;
    background: var(--wa-color-brand-fill-loud);
    color: var(--wa-color-brand-on-loud);
  }

  wa-button.graph-button--primary::part(base):hover {
    background: color-mix(in srgb, var(--wa-color-brand-fill-loud) 85%, var(--ns-theme-black));
  }

  wa-button.graph-button--primary[disabled]::part(base) {
    background: var(--ns-color-disabled-fill);
    color: var(--ns-color-disabled-text);
  }

  wa-button.graph-button--tint::part(base) {
    border-color: var(--wa-color-brand-border-normal);
    color: var(--wa-color-brand-on-quiet);
  }

  wa-button.graph-button--tint::part(base):hover {
    background: var(--wa-color-brand-fill-quiet);
    color: var(--wa-color-brand-on-quiet);
  }

  wa-button.ql-view-profile {
    margin-top: var(--wa-space-xs);

    font-size: var(--wa-font-size-xs);
  }

  wa-button.graph-button--pill::part(base) {
    border-radius: var(--wa-border-radius-pill);
  }

  wa-button.graph-button--block,
  wa-button.graph-button--block::part(base) {
    width: 100%;
  }

  wa-button.graph-button--grow {
    flex: 1;
  }

  wa-button.graph-button--grow::part(base) {
    width: 100%;
  }

  wa-button.graph-button--square {
    --wa-form-control-height: 30px;

    --graph-button-color: var(--wa-color-text-normal);

    font-size: var(--wa-font-size-m);
  }

  wa-button.graph-button--square::part(base) {
    width: 30px;
    padding: 0;
  }

  wa-button.graph-button--square.zoom-fit-btn {
    font-size: var(--wa-font-size-2xs);
    font-weight: 700;
  }

  ${nsReducedMotionStyles}
`;
